import React, { useState, useEffect, useRef } from 'react';

interface LazyItemProps {
  children: React.ReactNode;
  estimatedHeight: number;
  as?: 'tr' | 'div';
  className?: string;
  onClick?: () => void;
}

export const LazyItem = React.memo(function LazyItem({
  children,
  estimatedHeight,
  as: Component = 'div',
  className = '',
  onClick,
}: LazyItemProps) {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        rootMargin: '300px 0px', // Pre-load elements 300px before they enter viewport for ultra-seamless scrolling
        threshold: 0.01,
      }
    );

    const currentEl = containerRef.current;
    if (currentEl) {
      observer.observe(currentEl);
    }

    return () => {
      if (currentEl) {
        observer.unobserve(currentEl);
      }
    };
  }, []);

  if (!isVisible) {
    if (Component === 'tr') {
      return (
        <tr
          ref={containerRef as any}
          style={{ height: `${estimatedHeight}px` }}
          className={className}
        >
          <td colSpan={10} style={{ padding: 0 }} />
        </tr>
      );
    }
    return (
      <div
        ref={containerRef as any}
        style={{ height: `${estimatedHeight}px` }}
        className={className}
      />
    );
  }

  if (Component === 'tr') {
    return (
      <tr ref={containerRef as any} className={className} onClick={onClick}>
        {children}
      </tr>
    );
  }

  return (
    <div ref={containerRef as any} className={className} onClick={onClick}>
      {children}
    </div>
  );
});
